/**
 * What a playbook usually costs and takes, from runs that actually finished.
 *
 * The start page offered twelve tiles with no sense of either (#124). The
 * number has to be one the system has: the run list already holds every run
 * the viewer may see, with its cost and its two timestamps, so this reads it
 * rather than asking a second endpoint for figures the client can add up —
 * the same reasoning that keeps the Engagements tiles client-side.
 *
 * **Completed runs only, and at least two.** A failed run stopped early and
 * would pull the figure down; one run is an anecdote, not "typically". Below
 * that the tile quotes the playbook's ceiling instead, which is a promise the
 * system enforces rather than a guess about it.
 *
 * The median, because a single run that met its ceiling would drag a mean.
 *
 * **Runs on the model a new one would get, and only those.** A run costs
 * what its model charges, so a median over Claude Opus and Kimi runs together
 * is a figure for neither: the same playbook was twice as dear on one. With a
 * `models` filter, a run that names no provider (older than the choice) or
 * put its specialists on another model does not count.
 */

import type { RunSummary } from "./types";

export const MIN_RUNS = 2;

export interface Typical {
  runs: number;
  costUsd: number;
  seconds: number;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function typicalByPlaybook(
  runs: RunSummary[],
  models?: { provider: string; model: string },
): Map<string, Typical> {
  const by = new Map<string, { cost: number[]; seconds: number[] }>();
  for (const run of runs) {
    if (run.status !== "completed" || !run.playbook || !run.started_at || !run.finished_at) {
      continue;
    }
    if (models && !onModels(run, models)) continue;
    const seconds =
      (new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000;
    if (!(seconds > 0)) continue;
    const bucket = by.get(run.playbook) ?? { cost: [], seconds: [] };
    bucket.cost.push(run.cost_usd ?? 0);
    bucket.seconds.push(seconds);
    by.set(run.playbook, bucket);
  }
  const out = new Map<string, Typical>();
  for (const [key, { cost, seconds }] of by) {
    if (cost.length < MIN_RUNS) continue;
    out.set(key, { runs: cost.length, costUsd: median(cost), seconds: median(seconds) });
  }
  return out;
}

function onModels(run: RunSummary, models: { provider: string; model: string }): boolean {
  const specialists = run.subagent_model || run.model;
  return (
    run.provider === models.provider &&
    run.model === models.model &&
    specialists === models.model
  );
}
